import { hasAny, nonEmptyBullets, fmtRange, joinContact, joinLinks, getSectionMarkerAttrs } from "./utils";

// ATS-first: single column, sans-serif, no colors, no icons, plain bullets.
export default function ATSPlainTemplate({ resume }) {
  const { personal, summary, experience, education, skills, projects, certifications, languages } = resume;
  return (
    <div
      style={{
        fontFamily: "Arial, Helvetica, sans-serif",
        color: "#000000",
        background: "#ffffff",
        padding: "56px 60px",
        fontSize: 10,
        lineHeight: 1.4,
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
      }}
    >
      <div style={{ textAlign: "center", marginBottom: 14 }}>
        <div data-resume-h1 style={{ fontSize: 20, fontWeight: 700 }}>{personal.fullName || "Your Name"}</div>
        {personal.title && <div style={{ fontSize: 12 }}>{personal.title}</div>}
        <div data-resume-contact="true" style={{ fontSize: 10, marginTop: 4 }}>
          {[joinContact(personal), joinLinks(personal)].filter(Boolean).join(" | ")}
        </div>
      </div>

      {summary && <ATSSection title="PROFESSIONAL SUMMARY"><div>{summary}</div></ATSSection>}

      {skills?.length > 0 && (
        <ATSSection title="SKILLS">
          <div>{skills.join(", ")}</div>
        </ATSSection>
      )}

      {hasAny(experience, ["company", "role"]) && (
        <ATSSection title="EXPERIENCE">
          {experience.map((e, i) => {
            const bullets = nonEmptyBullets(e.bullets);
            if (!e.company && !e.role && bullets.length === 0) return null;
            return (
              <div key={e.id || i} data-page-item="true" style={{ marginBottom: 8 }}>
                <div style={{ fontWeight: 700 }}><span data-resume-jobtitle>{e.role}</span>{e.company ? <>, <span data-resume-company>{e.company}</span></> : ""}</div>
                <div style={{ fontStyle: "italic" }}>
                  {e.location && <span data-resume-location>{e.location}</span>}
                  {e.location && fmtRange(e.startDate, e.endDate) && " | "}
                  {fmtRange(e.startDate, e.endDate) && <span data-resume-date>{fmtRange(e.startDate, e.endDate)}</span>}
                </div>
                {bullets.length > 0 && (
                  <ul data-resume-workdesc style={{ margin: "3px 0 0 18px", padding: 0 }}>
                    {bullets.map((b, bi) => <li key={`${e.id || i}-b-${bi}`}>{b}</li>)}
                  </ul>
                )}
              </div>
            );
          })}
        </ATSSection>
      )}

      {hasAny(education, ["school", "degree"]) && (
        <ATSSection title="EDUCATION">
          {education.map((ed, i) => (ed.school || ed.degree) && (
            <div key={ed.id || i} style={{ marginBottom: 4 }}>
              <div><span style={{ fontWeight: 700 }}>{ed.degree}{ed.field ? `, ${ed.field}` : ""}</span> — {ed.school}</div>
              <div style={{ fontStyle: "italic" }}>{fmtRange(ed.startDate, ed.endDate)}</div>
              {ed.details && <div>{ed.details}</div>}
            </div>
          ))}
        </ATSSection>
      )}

      {hasAny(projects, ["name"]) && (
        <ATSSection title="PROJECTS">
          {projects.map((p, i) => p.name && (
            <div key={p.id || i} style={{ marginBottom: 4 }}>
              <div><span data-resume-projectname style={{ fontWeight: 700 }}>{p.name}</span>{p.tech ? ` — ${p.tech}` : ""}</div>
              {p.description && <div>{p.description}</div>}
            </div>
          ))}
        </ATSSection>
      )}

      {hasAny(certifications, ["name"]) && (
        <ATSSection title="CERTIFICATIONS">
          {certifications.map((c, i) => c.name && (
            <div key={c.id || i}>{c.name}{c.issuer ? `, ${c.issuer}` : ""}{c.date ? ` (${c.date})` : ""}</div>
          ))}
        </ATSSection>
      )}

      {languages?.some((l) => l.name?.trim()) && (
        <ATSSection title="LANGUAGES">
          {languages.map((l) => l.name?.trim() && (
            <div key={l.id}>{l.name}{l.level ? ` (${l.level})` : ""}</div>
          ))}
        </ATSSection>
      )}
    </div>
  );
}

function ATSSection({ title, children }) {
  return (
    <section {...getSectionMarkerAttrs(title)} style={{ marginBottom: 12 }}>
      <div data-resume-h2 style={{ fontWeight: 700, fontSize: 12, borderBottom: "1px solid #000", marginBottom: 4 }}>{title}</div>
      {children}
    </section>
  );
}
